import React from 'react';

interface LogoProps {
  variant?: 'header' | 'sidebar' | 'full';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Logo: React.FC<LogoProps> = ({ variant = 'full', size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: { logo: 'w-8 h-8', title: 'text-lg', subtitle: 'text-[10px]' },
    md: { logo: 'w-12 h-12', title: 'text-2xl', subtitle: 'text-xs' },
    lg: { logo: 'w-20 h-20', title: 'text-4xl', subtitle: 'text-sm' },
    xl: { logo: 'w-24 h-24', title: 'text-5xl', subtitle: 'text-base' }
  };

  const currentSize = sizeClasses[size as keyof typeof sizeClasses] || sizeClasses.md;

  // Common Premium Text Styles
  const titleClass = `${currentSize.title} font-serif font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-sm leading-none`;
  const subtitleClass = `${currentSize.subtitle} text-premium-gold/80 font-sans font-medium tracking-[0.2em] mt-1 uppercase`;

  // The Royal Crown + TU Monogram SVG
  const LogoSVG = (
    <svg
      viewBox="0 0 100 100"
      className={`${currentSize.logo} drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F5E089" />
          <stop offset="50%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8A6E18" />
        </linearGradient>
      </defs>

      {/* Crown Elements */}
      <path
        d="M20 35 L10 20 L30 35 L50 10 L70 35 L90 20 L80 35 Z"
        stroke="url(#goldGradient)"
        strokeWidth="3"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M20 35 L20 45 Q50 55 80 45 L80 35"
        stroke="url(#goldGradient)"
        strokeWidth="3"
        fill="none"
      />

      {/* T Monogram */}
      <path
        d="M35 55 L65 55 M50 55 L50 90"
        stroke="url(#goldGradient)"
        strokeWidth="6"
        strokeLinecap="square"
      />

      {/* U Monogram overlaying T slightly for style */}
      <path
        d="M35 65 L35 80 Q35 95 50 95 Q65 95 65 80 L65 65"
        stroke="url(#goldGradient)"
        strokeWidth="5"
        strokeLinecap="square"
        opacity="0.9"
      />

      {/* Tech Circuit Dots */}
      <circle cx="50" cy="10" r="3" fill="url(#goldGradient)" />
      <circle cx="10" cy="20" r="2" fill="url(#goldGradient)" />
      <circle cx="90" cy="20" r="2" fill="url(#goldGradient)" />
    </svg>
  );

  if (variant === 'header') {
    const headerSizeClasses = {
      sm: {
        svg: 'w-16 h-16',
        title: 'text-2xl',
        subtitle: 'text-[10px] tracking-[0.15em] mt-1',
        margin: 'mb-3'
      },
      md: {
        svg: 'w-24 h-24',
        title: 'text-3xl',
        subtitle: 'text-sm mt-2',
        margin: 'mb-4'
      },
      lg: {
        svg: 'w-32 h-32 md:w-40 md:h-40',
        title: 'text-4xl md:text-6xl',
        subtitle: 'text-base md:text-lg mt-3',
        margin: 'mb-6'
      }
    };

    // Default to lg for backward compatibility if size not strictly matched, 
    // but since we control size prop usually, we map 'md' to 'md', 'sm' to 'sm'.
    // The previous default implementation was HUGE (lg). 
    // If props.size is 'md' (default), we might want to keep it 'lg'-ish or actually 'md'? 
    // Let's make 'md' actually medium, and explicit 'lg' be the large one. 
    // But we need to ensure other pages using <Logo variant="header" /> (default size=md) don't shrink unexpectedly if they rely on it being huge.
    // The Landing page probably uses it.
    // Let's assume standard md should be smaller than the massive banner header.
    // However, to be safe, let's map 'md' to the intermediate size, and 'lg' to the original size.

    const currentHeaderSize = headerSizeClasses[size as keyof typeof headerSizeClasses] || headerSizeClasses.lg;

    return (
      <div className={`flex flex-col items-center justify-center ${className}`}>
        {/* Large Centered Logo */}
        <div className={`${currentHeaderSize.margin} transform hover:scale-105 transition-transform duration-500`}>
          {/* Use a larger SVG for header variant */}
          <svg
            viewBox="0 0 100 100"
            className={`${currentHeaderSize.svg} drop-shadow-[0_0_25px_rgba(212,175,55,0.5)]`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="goldGradientHeader" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F5E089" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#8A6E18" />
              </linearGradient>
            </defs>
            <path d="M20 35 L10 20 L30 35 L50 10 L70 35 L90 20 L80 35 Z" stroke="url(#goldGradientHeader)" strokeWidth="3" strokeLinejoin="round" fill="none" />
            <path d="M20 35 L20 45 Q50 55 80 45 L80 35" stroke="url(#goldGradientHeader)" strokeWidth="3" fill="none" />
            <path d="M35 55 L65 55 M50 55 L50 90" stroke="url(#goldGradientHeader)" strokeWidth="6" strokeLinecap="square" />
            <path d="M35 65 L35 80 Q35 95 50 95 Q65 95 65 80 L65 65" stroke="url(#goldGradientHeader)" strokeWidth="5" strokeLinecap="square" opacity="0.9" />
            <circle cx="50" cy="10" r="3" fill="url(#goldGradientHeader)" />
            <circle cx="10" cy="20" r="2" fill="url(#goldGradientHeader)" />
            <circle cx="90" cy="20" r="2" fill="url(#goldGradientHeader)" />
          </svg>
        </div>

        <div className="text-center">
          <h1 className={`${currentHeaderSize.title} font-serif font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-md leading-tight`}>
            TechUnity Global
          </h1>
          <p className={`${currentHeaderSize.subtitle} text-premium-gold/80 font-medium tracking-[0.3em] uppercase`}>
            Uniting Global Talent, Empowering Innovation
          </p>
        </div>
      </div>
    );
  }

  // Sidebar and Full variants
  return (
    <div className={`flex items-center gap-2 sm:gap-4 ${className}`}>
      <div className="relative flex-shrink-0">
        {LogoSVG}
      </div>
      <div className="flex flex-col">
        <div className={titleClass}>
          TechUnity
        </div>
        <div className={`${subtitleClass} hidden xs:block`}>
          Global
        </div>
      </div>
    </div>
  );
};

export default Logo;